/**
 * The browser half of sign-up attribution (ATTR-1), page by page:
 *
 *   takeHandoffArrival()  once per page load, before React renders
 *                         (src/entry-client.tsx): an address that carries
 *                         ?fly_src= (an in-app browser's hand-off) has its
 *                         touches merged in, and the value is taken off the
 *                         address before any tag or page view can read it
 *                         (left on inside an in-app browser, whose own Open in
 *                         browser menu takes the address as it stands). The
 *                         page it arrived on keeps no touch of its own: the
 *                         hand-off address is ours, not a source.
 *   noteTouch()           every page view (SignupAttribution, on each route):
 *                         keeps the page's own touch (store.ts).
 *   attributionPayload()  what the server is sent: both touches, the page,
 *                         the site session, the time zone, the in-app browser
 *                         and the ad cookies (_fbp, _fbc, _gcl_aw, _epik,
 *                         _rdt_uuid). The gbp-agent-portal ops
 *                         signup_attribution and start_signed_in take it.
 *   attributionSkipped()  where none of it runs: an embed, an agency's
 *                         white-label host, inside a frame (SignupAttribution
 *                         and the static landing's touch script, staticPage.ts).
 *
 * Never throws: tracking must not cost the page anything.
 */
import { isFramed } from '@/lib/inFrame';
import { maskPrivatePath } from '@/lib/privatePaths';
import { getSiteSessionId } from '@/lib/siteSession';
import { decodeTouches, HANDOFF_PARAM, withoutHandoff } from './handoff';
import { currentInAppBrowser, inAppLabel } from './inAppBrowser';
import { keepTouch, mergeCarried, readTouches, type Touches } from './store';
import { touchFromPage, type Touch } from './touch';

/** The ad cookies read at sign-up: Meta's browser and click ids, Google's click, Pinterest's and Reddit's. */
export const AD_COOKIES = ['_fbp', '_fbc', '_gcl_aw', '_epik', '_rdt_uuid'] as const;

/** Nothing here on an embed, an agency's white-label host (server.js writes __AGENCY_BRANDING__) or inside a frame. */
export function attributionSkipped(pathname: string): boolean {
  if (typeof window === 'undefined') return true;
  return pathname.startsWith('/embed') || !!(window as { __AGENCY_BRANDING__?: unknown }).__AGENCY_BRANDING__ || isFramed();
}

let arrivalRead = false;
let skipArrivalPage = false;

/** For the tests: the next call reads its address as a fresh load would. */
export function resetArrival(): void {
  arrivalRead = false;
  skipArrivalPage = false;
}

/**
 * The hand-off's arrival, once per page load: the carried touches merged in,
 * and fly_src taken off the address (outside an in-app browser). True when
 * the address carried them.
 */
export function takeHandoffArrival(now = Date.now()): boolean {
  if (arrivalRead) return false;
  arrivalRead = true;
  try {
    const loc = window.location;
    const carried = decodeTouches(new URLSearchParams(loc.search).get(HANDOFF_PARAM), now);
    if (!carried) return false;
    mergeCarried(carried, now);
    skipArrivalPage = true;
    if (!currentInAppBrowser()) {
      try {
        window.history.replaceState(window.history.state, '', withoutHandoff(loc.href));
      } catch {
        /* the value stays on the address; reading it twice changes nothing */
      }
    }
    return true;
  } catch {
    return false;
  }
}

/** One page view's touch kept; returns what is kept now. */
export function noteTouch(now = Date.now()): Touches {
  try {
    // A load the boot did not see yet (the dev entry, a test) is read here.
    takeHandoffArrival(now);
    if (skipArrivalPage) {
      skipArrivalPage = false;
      return readTouches(now);
    }
    const loc = window.location;
    const iab = currentInAppBrowser();
    const touch: Touch = touchFromPage({
      pathname: loc.pathname,
      search: loc.search,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      ownHost: loc.host,
      now,
      session: getSiteSessionId(),
      iab: inAppLabel(iab),
    });
    return keepTouch(touch, now);
  } catch {
    return readTouches(now);
  }
}

/** The ad cookies this browser holds, by name. */
export function adCookies(cookieString?: string): Partial<Record<(typeof AD_COOKIES)[number], string>> {
  const out: Partial<Record<(typeof AD_COOKIES)[number], string>> = {};
  try {
    const raw = cookieString ?? (typeof document !== 'undefined' ? document.cookie : '');
    for (const part of raw.split(';')) {
      const eq = part.indexOf('=');
      if (eq === -1) continue;
      const name = part.slice(0, eq).trim();
      if (!(AD_COOKIES as readonly string[]).includes(name)) continue;
      let value = part.slice(eq + 1).trim();
      try {
        value = decodeURIComponent(value);
      } catch {
        /* kept as it is */
      }
      if (value && value.length <= 500) out[name as (typeof AD_COOKIES)[number]] = value;
    }
  } catch {
    /* no cookies to read */
  }
  return out;
}

export interface AttributionPayload {
  first: Touch | null;
  last: Touch | null;
  page: string;
  session: string;
  tz: string | null;
  iab: string | null;
  cookies: Partial<Record<(typeof AD_COOKIES)[number], string>>;
}

/** What the server is sent about this visitor, now. Never throws: the sign-up it rides with must not stop. */
export function attributionPayload(now = Date.now()): AttributionPayload {
  try {
    const kept = readTouches(now);
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      tz = null;
    }
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    return {
      first: kept.first,
      last: kept.last,
      page: maskPrivatePath(pathname),
      session: getSiteSessionId(),
      tz,
      iab: inAppLabel(currentInAppBrowser()),
      cookies: adCookies(),
    };
  } catch {
    return { first: null, last: null, page: '/', session: 'no_session', tz: null, iab: null, cookies: {} };
  }
}
